import React, { useContext } from 'react'
import { useParams } from 'react-router-dom'
import { DynamicPowerPriceWidgetData } from 'src/Libs/dashboard-widgets/hooks/use-data-for-dpp-widget'
import useModals from 'src/Hooks/useModals'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { DynamicPowerPricesEnabledContext } from '../../../dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { useDynamicEmsData } from 'src/Libs/dynamic-power-prices/hooks/dynamic-ems/use-dynamic-ems-data'
import { useNewRelic } from '@e3dc-react/shell/Libs/observability-tool'
import { fromDate, toDate } from 'src/Libs/dashboard-widgets/utils/dateUtils'
import DynamicPowerPriceWidget from './DynamicPowerPriceWidget'

type Props = DynamicPowerPriceWidgetData

/**
 * Component for a chart widget that shows the variable dynamic power price
 */
const DynamicPowerPriceWidgetContainer: React.FC<Props> = ({
  powerPricesForWidget,
  currentPowerPriceValues,
  powerPricesForModal,
  lastFetchedTimestampForModal,
  dynamicPricesSettings,
  handleDynamicPricesSettingsUpdate,
  isDynamicPowerPriceWidgetDataLoading,
}) => {
  const { pageActions } = useNewRelic()
  const { storageId, installationId } = useParams<{ storageId: string; installationId: string }>()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()
  const { isDynamicPowerPriceEnabledForBattery, isDynamicPowerPriceEnabledForWallboxes } = useContext(DynamicPowerPricesEnabledContext)
  const { openPowerPriceChartModal } = useModals()
  const { data, isLoading: isDynamicEmsDataLoading } = useDynamicEmsData(installationId, fromDate.format(), toDate.format())

  const wallboxLimit = Number(dynamicPricesSettings?.priceLimitWallbox ?? 0)
  const batteryLimit = Number(dynamicPricesSettings?.priceLimitBattery ?? 0)

  const handleOpenPowerPriceChartModal = (): void => {
    openPowerPriceChartModal({
      storageId,
      installationId,
      wallboxLimit,
      batteryLimit,
      hasInstallationWritingRights,
      powerPrices: powerPricesForModal,
      currentPowerPriceValues: currentPowerPriceValues,
      lastFetchedTimestamp: lastFetchedTimestampForModal,
      isPowerPricesLoading: isDynamicPowerPriceWidgetDataLoading,
      dynamicPricesSettings,
      handleDynamicPricesSettingsUpdate,
    })
  }

  return (
    <DynamicPowerPriceWidget
      currentPowerPriceValues={currentPowerPriceValues}
      data={data}
      dynamicPricesSettings={dynamicPricesSettings}
      handleDynamicPricesSettingsUpdate={handleDynamicPricesSettingsUpdate}
      handleOnClick={() => pageActions.componentClicked({ componentName: 'DynamicPowerPriceWidget' })}
      handleOpenPowerPriceChartModal={handleOpenPowerPriceChartModal}
      isDynamicPowerPriceEnabledForBattery={isDynamicPowerPriceEnabledForBattery}
      isDynamicPowerPriceEnabledForWallboxes={isDynamicPowerPriceEnabledForWallboxes}
      isDynamicPowerPriceWidgetDataLoading={isDynamicPowerPriceWidgetDataLoading}
      isDynamicEmsDataLoading={isDynamicEmsDataLoading}
      powerPricesForWidget={powerPricesForWidget}
      lastFetchedTimestampForModal={lastFetchedTimestampForModal}
      powerPricesForModal={powerPricesForModal}
      wallboxLimit={wallboxLimit}
      batteryLimit={batteryLimit}
    />
  )
}

export default DynamicPowerPriceWidgetContainer
